import type { Building } from "building-types/building/buildingTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { DocForeignKeyTable } from "building-types/doc/docForeignKeyTable";
import type { ComponentOwnedMemberEntityType } from "building-types/handles/docEntityHandleTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { RelatedRoomInfo } from "building-types/rooms/relatedRoomsMapTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import { parseEntityInfoFromHandleHash } from "doc/entity/docEntityHandleHash";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import memo from "../utils/memo";

// For a given source room ID, creates an array of all the related rooms that have that source.
export function buildRelatedRoomsMap(
  rooms: Table<Room>,
  componentMembers: DocForeignKeyTable<ComponentOwnedMemberEntityType, ForeignEntityId<"COMPONENT">>,
): { readonly [roomId: string]: ReadonlyArray<RelatedRoomInfo> } {
  const baseIdToRoomInfos: { [baseId: string]: RelatedRoomInfo[] } = {};

  // Create a list of rooms that belong to components for efficient lookup.
  const roomsInComponents = new Set<string>();
  for (const hash in componentMembers) {
    const entityInfo = parseEntityInfoFromHandleHash(hash);
    if (entityInfo && entityInfo.entityType === "ROOM") {
      roomsInComponents.add(entityInfo.entityId);
    }
  }

  for (const id in rooms) {
    const room = rooms[id];
    if (!room) {
      continue;
    }
    const baseId = getSourceRoomId(room);
    const componentInstanceChain = room.componentTransform?.componentInstanceChain;

    // Skip rooms that do not belong to a component.
    if (!roomsInComponents.has(baseId)) {
      continue;
    }

    // Detect mirrored room orientation, accounting for nested instance mirroring
    const mirrored = isCombinedGlobalInstanceMirror(false, room);
    baseIdToRoomInfos[baseId] ||= [];
    baseIdToRoomInfos[baseId].push({ roomId: room.id, mirrored, componentInstanceChain });
  }

  return baseIdToRoomInfos;
}

function selectRelatedRoomsMap({ doc: { componentMembers }, enabledFeatures, rooms }: Building) {
  return {
    componentMembers,
    enabledFeatures,
    rooms,
  };
}

function generateRelatedRoomsMap({
  componentMembers,
  enabledFeatures,
  rooms,
}: ReturnType<typeof selectRelatedRoomsMap>): Partial<Pick<Building, "relatedRoomsMap">> {
  // Only generate related rooms map when modelComponents feature is enabled.
  if (!enabledFeatures.modelComponents) {
    return {};
  }

  // Generate the related rooms map
  const relatedRoomsMap = buildRelatedRoomsMap(rooms, componentMembers);

  return {
    relatedRoomsMap,
  };
}

export default memo(selectRelatedRoomsMap, generateRelatedRoomsMap);
